import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MonacoEditorService} from "../../services/monaco-editor/monaco-editor.service";
import * as shortid from 'shortid';
import * as monaco from 'monaco-editor';
import {MonacoEditorMode} from "./monaco-editor.mode";
import {MonacoEditorOptions} from "./monaco-editor-options";
import IStandaloneEditorConstructionOptions = monaco.editor.IStandaloneEditorConstructionOptions;
import IStandaloneCodeEditor = monaco.editor.IStandaloneCodeEditor;
import {FeatureFlagsService} from "../../services/feature-flag/feature-flags.service";
@Component({
  selector: 'sb-monaco-editor',
  templateUrl: './monaco-editor.component.html',
  styleUrls: ['./monaco-editor.component.scss']
})
export class MonacoEditorComponent implements OnInit, OnDestroy {

  DEFAULT_MODE: MonacoEditorMode = 'text';

  CUSTOM_MENU_GROUP = '8_custom';

  LANGUAGE_MAPPING = {
    'text': 'text',
    'xml': 'xml',
    'html': 'html',
    'scss': 'scss',
    'css': 'css',
    'json': 'json',
    'javascript': 'javascript',
    'typescript': 'typescript',
    'server-script': 'typescript',
    'client-script': 'javascript',
    'angular-template': 'html',
    'angular-component': 'typescript',
    'angular-scss': 'scss',
    'sql': 'sql',
  };

  SUFFIX_MAPPING = {
    'text': 'txt',
    'xml': 'xml',
    'html': 'html',
    'scss': 'scss',
    'css': 'css',
    'json': 'json',
    'javascript': 'js',
    'typescript': 'ts',
    'sql': 'sql',
  };

  RENDERER_PROPERTIES = [
    'mode',
    'code.mode',
    'code.theme',
    'code.tabSize',
    'code.softTabs',
    'code.autoComplete',
    'code.gotoLine',
    'code.readOnly',
    'code.json.schemas',
    'code.json.uri',
    'code.script.globals',
    'code.script.libraries',
  ];

  @ViewChild('container', {static: false})
  editorContainer: ElementRef;

  @ViewChild('editor', {static: false})
  editorComponent;

  editor: IStandaloneCodeEditor;

  @Input()
  rendererProperties: object;

  @Input()
  properties: object;

  @Input()
  saveCommand: {fn: () => void, label?: string};

  @Input()
  onChangeCommand: (value: string) => void;

  @Input()
  onTouchedCommand: () => void;

  // Return a thunk that sets new code. This is because change detection is not working properly
  // if you just update the code input itself.
  @Input()
  get setCode(): (string) => void {
    const self = this;
    return (newValue: string) => {
      if (self.editor.getValue() !== newValue) {
        self.editor.setValue(newValue ?? "");
      }
    };
  }

  @Input()
  options: MonacoEditorOptions | string;

  @Input()
  mode: MonacoEditorMode = this.DEFAULT_MODE;

  @Input()
  jsonSchemas: string;

  @Input()
  jsonUri: string;

  @Input()
  scriptGlobals: string;

  @Input()
  scriptLibraries: string;

  @Input()
  code: string = '';

  // Used for i18n prefix
  @Input()
  recordId: string;

  // Used for i18n prefix and editScope
  @Input()
  tableClass: string;

  // Used for i18n prefix and editScope
  @Input()
  fieldName: string;

  editorOptions: IStandaloneEditorConstructionOptions;

  parsedOptions: MonacoEditorOptions;

  initialScrollPosition = 0;

  sizeClass = "";

  size = "";

  showParameters = false;

  showParametersSet = false;

  parameters: any[] = [];

  cssClass = "sb-monaco-editor sb-monaco-editor-hide-parameters";

  fullscreen = false;

  constructor(private hostElement: ElementRef<HTMLElement>,
              private monacoEditorService: MonacoEditorService,
              private featureFlagsService: FeatureFlagsService) {
  }

  async ngOnInit() {
    this.monacoEditorService.registerEditor(this);
    await this.init();
  }

  async ngOnDestroy() {
    this.monacoEditorService.deregisterEditor(this);
  }

  private async init() {
    await this.monacoEditorService.monacoReady();

    let parsedOptions: MonacoEditorOptions;

    if (this.options) {
      parsedOptions = typeof this.options === 'string' ? JSON.parse(this.options) : this.options;
    } else {
      parsedOptions = {
        mode: this.mode,
        theme: 'vs',
        tabSize: 4,
        softTabs: true,
        autoComplete: true,
        gotoLine: 0,
        readOnly: false,
        jsonSchemas: this.jsonSchemas ? this.jsonSchemas.split(/,/) : [],
        jsonUri: this.jsonUri,
        scriptGlobals: this.scriptGlobals ? this.scriptGlobals.split(/,/) : [],
        scriptLibraries: this.scriptLibraries ? this.scriptLibraries.split(/,/) : [],
        size: ''
      }
    }

    // Options that come from the view definition renderer properties or the field definition renderer properties
    const propVars = [this.rendererProperties];
    propVars.forEach((props) => {
      if (props) {
        const mode = props['code.mode'] || props['mode'];
        const theme = props['code.theme'];
        const tabSize = props['code.tabSize'];
        const softTabs = props['code.softTabs'];
        const autoComplete = props['code.autoComplete'];
        const gotoLine = props['code.gotoLine'];
        const readOnly = props['code.readOnly'];
        const jsonSchemas = props['code.json.schemas'] ? props['code.json.schemas'].replaceAll(" ", "").split(/,/) : null;
        let jsonUri = props['code.json.uri'] ? props['code.json.uri'] : null;
        let scriptGlobals = props['code.script.globals'] ? props['code.script.globals'].replaceAll(" ", "").split(/,/) : null;
        let scriptLibraries = props['code.script.libraries'] ? props['code.script.libraries'].replaceAll(" ", "").split(/,/) : null;
        let size = props['code.size'] ? props['code.size'] : '';

        parsedOptions.mode = mode || parsedOptions.mode;
        parsedOptions.theme = theme || parsedOptions.theme;
        parsedOptions.tabSize = tabSize || parsedOptions.tabSize;
        parsedOptions.softTabs = softTabs || parsedOptions.softTabs;
        parsedOptions.autoComplete = autoComplete || parsedOptions.autoComplete;
        parsedOptions.gotoLine = gotoLine || parsedOptions.gotoLine;
        parsedOptions.readOnly = readOnly || parsedOptions.readOnly;
        parsedOptions.jsonSchemas = jsonSchemas || parsedOptions.jsonSchemas;
        parsedOptions.jsonUri = jsonUri || parsedOptions.jsonUri;
        parsedOptions.scriptGlobals = scriptGlobals || parsedOptions.scriptGlobals;
        parsedOptions.scriptLibraries = scriptLibraries || parsedOptions.scriptLibraries;
        parsedOptions.size = size || parsedOptions.size;
      }
    });

    // Initial scroll position cannot be set when editor created.
    this.initialScrollPosition = parsedOptions.gotoLine;

    this.parsedOptions = parsedOptions;

    let overrideOptions: any = {};

    this.mode = parsedOptions.mode;

    this.editorOptions = Object.assign({},{
      automaticLayout: true,
      scrollBeyondLastLine: false,
      theme: parsedOptions.theme,
      tabSize: parsedOptions.tabSize,
      insertSpaces: parsedOptions.softTabs,
      readOnly: parsedOptions.readOnly,

      // This is a nonstandard use of model by the ngx-monaco-editor
      model: {
        // @ts-ignore
        value: this.code,
        language: this.LANGUAGE_MAPPING[this.mode],
        uri: this.monacoEditorService.uriParse(`inmemory://${shortid.generate()}/${shortid.generate()}.${this.SUFFIX_MAPPING[this.LANGUAGE_MAPPING[this.mode]]}`)
      }

    }, overrideOptions);

    // There is currently no simple way to turn all of intellisense off
    if (!parsedOptions.autoComplete) {
      this.editorOptions.suggestOnTriggerCharacters = false;
      this.editorOptions.quickSuggestions = false;
      this.editorOptions.suggest = {
        showStatusBar: true, // what should this be?
        // hideStatusBar: false, // this is showStatusBar in ngx-monaco-editor 12
        // maxVisibleSuggestions: 0, // this seems to have disappeared in ngx-monaco-editor 12
        showClasses: false,
        showColors: false,
        showConstants: false,
        showConstructors: false,
        showEnumMembers: false,
        showEnums: false,
        showEvents: false,
        showFields: false,
        showFiles: false,
        showFolders: false,
        showFunctions: false,
        showIcons: false,
        showInterfaces: false,
        showKeywords: false,
        showMethods: false,
        showModules: false,
        showOperators: false,
        showProperties: false,
        showReferences: false,
        showSnippets: false,
        showStructs: false,
        showTypeParameters: false,
        showUnits: false,
        showValues: false,
        showVariables: false,
        showWords: false,
      };
    }

    this.sizeClass = parsedOptions.size ? `sb-monaco-editor-wrapper-${parsedOptions.size}` : '';

    this.size = parsedOptions.size;

    this.configureForEditor();
  }

  // Customise the specific editor instance in this function. This will override the ones defined in MonacoEditorService
  // and passed in via editorOptions above.
  onInit(editor: IStandaloneCodeEditor) {
    this.editor = editor;
    this.editorComponent.registerOnChange((value) => this.onChange(value));
    this.editorComponent.registerOnTouched(() => this.onTouched());

    // Add save command
    if (this.saveCommand) {
      editor.addAction({
        id: 'sb-save-command',
        label: this.saveCommand.label,
        keybindings: [
          monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
        ],
        precondition: null,
        keybindingContext: null,
        contextMenuGroupId: this.CUSTOM_MENU_GROUP,
        contextMenuOrder: 1.1,

        run: (ed) => {
          this.saveCommand.fn();
        }
      });
    }

    // Add fullscreen mode
    editor.addAction({
      id: 'sb-fullscreen-command',
      label: 'Toggle fullscreen',
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.F11,
      ],
      precondition: null,
      keybindingContext: null,
      contextMenuGroupId: this.CUSTOM_MENU_GROUP,
      contextMenuOrder: 1.2,

      run: (ed) => {
        this.setFullScreen(!this.fullscreen);
      }
    });

    // Add i18n prefix
    editor.addAction({
      id: 'sb-i18n-command',
      label: 'Insert i18n prefix',
      keybindings: [
        monaco.KeyMod.CtrlCmd | monaco.KeyCode.F12,
      ],
      precondition: null,
      keybindingContext: null,
      contextMenuGroupId: this.CUSTOM_MENU_GROUP,
      contextMenuOrder: 1.3,

      run: (ed) => {
        let insertPrefix = false;
        let atAtPrefix = "@@";
        if (this.mode === "angular-template") {
          insertPrefix = true;
        } else if (this.mode == "angular-component" || this.mode == "server-script" || this.mode == "client-script") {
          insertPrefix = true;
        }

        if (insertPrefix) {
          const prefix = ["ng", this.recordId, this.tableClass, this.fieldName].filter(s => !!s).join(".") + ".";
          const selection = editor.getSelection();
          let selectedText = ed.getModel().getValueInRange(selection);
          if (selectedText.startsWith(atAtPrefix)) {
            selectedText = selectedText.substring(atAtPrefix.length);
          }
          let newText = prefix + selectedText;
          if (!newText.startsWith(atAtPrefix)) {
            newText = atAtPrefix + newText;
          }
          ed.executeEdits("",
            [
              {
                range: new monaco.Range(selection.startLineNumber,
                  selection.startColumn,
                  selection.endLineNumber,
                  selection.endColumn),
                text: newText
              }
            ]
          );
          const newSel = editor.getSelection();
          ed.setSelection(new monaco.Selection(newSel.endLineNumber, newSel.endColumn, newSel.endLineNumber, newSel.endColumn));
        }
      }
    });

    // Cannot set this at editor creation time.
    editor.setScrollPosition({scrollLeft: 0, scrollTop: this.initialScrollPosition});

    editor.onDidBlurEditorWidget(() => {
      this.monacoEditorService.blurEditor(this);
    });

    editor.onDidFocusEditorWidget(() => {
      this.monacoEditorService.focusEditor(this);
    });

    // To make this editor fit properly in its container we need to lay it out after a short period.
    editor.layout();
    setTimeout(() => editor.layout(), 100);
  }

  configureForEditor() {
    switch (this.mode) {
      case 'sql': {
        this.monacoEditorService.configureForSql();
      }
        break;
      case 'text': {
        this.monacoEditorService.configureForText();
      }
        break;
      case 'xml': {
        this.monacoEditorService.configureForXml();
      }
        break;
      case 'html': {
        this.monacoEditorService.configureForHtml();
      }
        break;
      case 'scss': {
        this.monacoEditorService.configureForScss();
      }
        break;
      case 'css': {
        this.monacoEditorService.configureForCss();
      }
        break;
      case 'json': {
        const schemas: string[] = this.parsedOptions.jsonSchemas;
        const uri: string = this.parsedOptions.jsonUri;
        this.monacoEditorService.configureForJson(schemas, uri);
      }
        break;
      case 'javascript': {
        this.monacoEditorService.configureForJavaScript();
      }
        break;
      case 'typescript': {
        this.monacoEditorService.configureForTypeScript();
      }
        break;
      case 'server-script': {
        const globals: string[] = this.parsedOptions.scriptGlobals;
        const libraries: string[] = this.parsedOptions.scriptLibraries;
        this.monacoEditorService.configureForServerScript(globals, libraries, `${this.tableClass}.${this.fieldName}`);
        this.configureServerScriptParameters();
      }
        break;
      case 'client-script': {
        const globals: string[] = this.parsedOptions.scriptGlobals;
        const libraries: string[] = this.parsedOptions.scriptLibraries;
        this.monacoEditorService.configureForClientScript(globals, libraries, `${this.tableClass}.${this.fieldName}`);
      }
        break;
      case 'angular-template': {
        this.monacoEditorService.configureForAngularTemplate();
      }
        break;
      case 'angular-component': {
        this.monacoEditorService.configureForAngularComponent();
      }
        break;
      case 'angular-scss': {
        this.monacoEditorService.configureForAngularScss();
      }
        break;
      default:
        console.error(`MonacoEditorComponent.init: unknown mode '${this.mode}'`);
    }
  }

  deconfigureForEditor() {
  }

  private async configureServerScriptParameters() {
    if (!this.showParametersSet) {
      const editContext = `${this.tableClass}.${this.fieldName}`;
      this.parameters = await this.monacoEditorService.loadServerScriptEditorScope(editContext);
      this.showParameters = this.parameters.length > 0;
      this.parameters.forEach(p => {
        let combinedDocumentation = 'No documentation available';
        const hasDocumentation = p.Documentation?.length > 0;
        const hasTableRecords = p.TableRecords?.length > 0;
        if (hasDocumentation || hasTableRecords) {
          combinedDocumentation =
            (hasDocumentation ? p.Documentation : "") +
            (hasTableRecords ? (hasDocumentation ? " - " : "") + "contains a value of type <b>TableRecord</b>" : "" )
        }
        p.CombinedDocumentation = combinedDocumentation;
      });
      this.cssClass = "sb-monaco-editor " + (this.showParameters ? "sb-monaco-editor-show-parameters" : "sb-monaco-editor-hide-parameters") + (this.size ? `-${this.size}` : "");
      this.showParametersSet = true;
    }
  }

  // Every change to the code in the editor will trigger this.
  private onChange(value: string) {
    this.onChangeCommand && this.onChangeCommand(value);
  }

  // When the editor loses focus it will trigger this.
  private onTouched() {
    this.onTouchedCommand && this.onTouchedCommand();
  }

  private setFullScreen(fullscreen: boolean) {
    this.fullscreen = fullscreen;

    // Stacking order means that some elements need to toggle visibility.
    if (fullscreen) {
      const siteHeader = document.getElementsByClassName("site-header")[0] as HTMLElement;
      if (siteHeader) {
        siteHeader.style.display = 'none';
      }
      this.hostElement.nativeElement.children[0].classList.add('fullscreen');
    } else {
      const siteHeader = document.getElementsByClassName("site-header")[0] as HTMLElement;
      if (siteHeader) {
        siteHeader.style.display = null;
      }
      this.hostElement.nativeElement.children[0].classList.remove('fullscreen');
    }
  }
}
